/**
 * hooks/useClientMerge.ts
 *
 * Powers the manual client merge workflow.
 * Detects conflicts between two client records and executes the merge RPC
 * with explicit user-provided field resolutions.
 * Created Apr 2026.
 */

import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import type { FieldConflict, FieldResolutions } from '../types/merge';
import type { Database } from '../types/database';

type Client = Database['public']['Tables']['clients']['Row'];

// Coupled to the 'undo window has expired' message thrown by the undo_client_merge
// RPC. Update here if the RPC error text changes.
function isUndoWindowExpired(err: unknown): boolean {
  if (err instanceof Error) {
    return /expir/i.test(err.message);
  }
  return false;
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'Email',
  phone: 'Téléphone',
  notes: 'Notes',
};

interface UseClientMergeParams {
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  onMergeComplete?: () => void;
}

export function useClientMerge({ showToast, onMergeComplete }: UseClientMergeParams) {
  const [merging, setMerging] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const detectConflicts = useCallback(
    (target: Client, source: Client): FieldConflict[] => {
      const conflictFields = ['first_name', 'last_name', 'email', 'phone', 'notes'] as const;
      const conflicts: FieldConflict[] = [];

      for (const field of conflictFields) {
        const tv = target[field] ?? null;
        const sv = source[field] ?? null;
        if (tv !== null && sv !== null && tv !== sv) {
          conflicts.push({
            field,
            label: FIELD_LABELS[field] ?? field,
            targetValue: tv,
            sourceValue: sv,
          });
        }
      }

      return conflicts;
    },
    []
  );

  const performMerge = useCallback(
    async (
      targetClientId: string,
      sourceClientId: string,
      resolutions: FieldResolutions
    ): Promise<string | null> => {
      setMerging(true);
      try {
        const { data, error } = await supabase.rpc('merge_clients', {
          p_target_client_id: targetClientId,
          p_source_client_id: sourceClientId,
          p_field_resolutions: resolutions as Record<string, string>,
        });
        if (error) throw error;
        showToast('Fiches clients fusionnées avec succès', 'success');
        onMergeComplete?.();
        return data as string;
      } catch (err) {
        captureError(err, { hook: 'useClientMerge', action: 'performMerge' });
        showToast('Erreur lors de la fusion', 'error');
        return null;
      } finally {
        setMerging(false);
      }
    },
    [showToast, onMergeComplete]
  );

  const undoMerge = useCallback(
    async (mergeId: string): Promise<boolean> => {
      setUndoing(true);
      try {
        const { error } = await supabase.rpc('undo_client_merge', {
          p_merge_id: mergeId,
        });
        if (error) throw error;
        showToast('Fusion annulée - les deux fiches ont été restaurées', 'success');
        return true;
      } catch (err) {
        captureError(err, { hook: 'useClientMerge', action: 'undoMerge' });
        const message = isUndoWindowExpired(err)
          ? 'La fenêtre d\'annulation de 30 jours est expirée'
          : 'Erreur lors de l\'annulation de la fusion';
        showToast(message, 'error');
        return false;
      } finally {
        setUndoing(false);
      }
    },
    [showToast]
  );

  return { merging, undoing, detectConflicts, performMerge, undoMerge };
}
